import { TReduxActions } from '../../../common/actions';
import { EFavoritesActionType } from '../../../common/actions/favorites';
import { EFeaturesActionType } from '../../../common/actions/features/types';
import { EFetchMapDataActionType } from '../../../common/actions/fetchMapData/types';
import { EAddToFavoritesActionTypes } from '../../../common/actions/newFavorites/addToFavorite';
import { ERemoveFromFavoritesActionTypes } from '../../../common/actions/newFavorites/removeFromFavorite';
import { EMarkAsViewedActionType } from '../../../common/actions/viewed';
import { IOffer } from '../../../common/repositories/dailyrent/entities/offer/Offer';
import { IFeaturesMap, ITilesMap, TFeature } from '../../../common/types/map';
import { IPrecisionFeatures } from '../../../common/types/redux/store/features';

export const defaultState: IPrecisionFeatures = {};

export function featuresReducer(state: IPrecisionFeatures = defaultState, action: TReduxActions): IPrecisionFeatures {
  switch (action.type) {
    case EFetchMapDataActionType.Succeed: {
      const tiles = action.payload.tiles || [];
      const precision = action.payload.precision as number;

      const featuresMap = action.payload.clusters || {};

      const uniqueTiles: ITilesMap = {};
      tiles.forEach(([x, y]) => {
        uniqueTiles[`${x},${y}`] = [x, y];
      });

      return {
        ...state,
        [precision]: {
          ...state[precision],
          features: featuresMap,
          tiles: uniqueTiles,
        },
      };
    }
    case EFetchMapDataActionType.Failed: {
      return defaultState;
    }

    case EAddToFavoritesActionTypes.Success:
    case EFavoritesActionType.Added: {
      return changeFavoriteValue(state, action.payload.offerId, true);
    }

    case ERemoveFromFavoritesActionTypes.Success:
    case EFavoritesActionType.Removed: {
      return changeFavoriteValue(state, action.payload.offerId, false);
    }

    case EMarkAsViewedActionType.Succeed: {
      return markOffersViewed(state, action.payload.cianIds);
    }

    case EFeaturesActionType.FeaturePropertiesUpdated: {
      const { precision, featureId, properties } = action.payload;
      const selectedFeature = state[precision]?.features[featureId];

      if (selectedFeature) {
        return {
          ...state,
          [precision]: {
            ...state[precision],
            features: {
              ...state[precision].features,
              [featureId]: {
                ...selectedFeature,
                properties: {
                  ...selectedFeature.properties,
                  ...properties,
                },
              },
            },
          },
        };
      }

      return state;
    }

    default:
      return state;
  }
}

function changeFavoriteValue(state: IPrecisionFeatures, offerId: number, value: boolean): IPrecisionFeatures {
  let hasChanges = false;
  const nextState: IPrecisionFeatures = {};

  for (const precision of Object.keys(state)) {
    const bucket = state[Number(precision)];
    const nextFeatures = updateFeaturesFavorite(bucket.features, offerId, value);

    nextState[Number(precision)] = nextFeatures === bucket.features ? bucket : { ...bucket, features: nextFeatures };
    hasChanges = hasChanges || nextFeatures !== bucket.features;
  }

  return hasChanges ? nextState : state;
}

function updateFeaturesFavorite(features: IFeaturesMap, offerId: number, value: boolean): IFeaturesMap {
  let hasChanges = false;
  const nextFeatures: IFeaturesMap = {};

  for (const id of Object.keys(features)) {
    const nextFeature = updateFeatureFavorite(features[id], offerId, value);

    nextFeatures[id] = nextFeature;
    hasChanges = hasChanges || nextFeature !== features[id];
  }

  return hasChanges ? nextFeatures : features;
}

// Объявление одно и то же на всех уровнях зума: помечаем его везде, иначе при смене зума
// до ответа карты пин из кэша другого уровня показывался бы непросмотренным.
function markOffersViewed(state: IPrecisionFeatures, cianIds: number[]): IPrecisionFeatures {
  const viewedIds = new Set(cianIds);
  let hasChanges = false;
  const nextState: IPrecisionFeatures = {};

  for (const precision of Object.keys(state)) {
    const bucket = state[Number(precision)];
    const nextFeatures = markFeaturesOffersViewed(bucket.features, viewedIds);

    nextState[Number(precision)] = nextFeatures === bucket.features ? bucket : { ...bucket, features: nextFeatures };
    hasChanges = hasChanges || nextFeatures !== bucket.features;
  }

  return hasChanges ? nextState : state;
}

function markFeaturesOffersViewed(features: IFeaturesMap, viewedIds: Set<number>): IFeaturesMap {
  let hasChanges = false;
  const nextFeatures: IFeaturesMap = {};

  for (const id of Object.keys(features)) {
    const nextFeature = markFeatureOffersViewed(features[id], viewedIds);

    nextFeatures[id] = nextFeature;
    hasChanges = hasChanges || nextFeature !== features[id];
  }

  return hasChanges ? nextFeatures : features;
}

function markFeatureOffersViewed(feature: TFeature, viewedIds: Set<number>): TFeature {
  const { offers } = feature.properties;
  const isTarget = (offer: IOffer) => !offer.isViewed && viewedIds.has(offer.cianId);

  if (!offers?.some(isTarget)) {
    return feature;
  }

  return {
    ...feature,
    properties: {
      ...feature.properties,
      offers: offers.map(offer => (isTarget(offer) ? { ...offer, isViewed: true } : offer)),
    },
  };
}

function updateFeatureFavorite(feature: TFeature, offerId: number, value: boolean): TFeature {
  const { offers } = feature.properties;
  const hasTarget = offers?.some(offer => offer.realtyId === offerId && offer.isFavorite !== value);

  if (!hasTarget) {
    return feature;
  }

  return {
    ...feature,
    properties: {
      ...feature.properties,
      offers: offers.map(offer => (offer.realtyId === offerId ? { ...offer, isFavorite: value } : offer)),
    },
  };
}
